import React, { useState } from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import {
  SubAssembly,
  HardwareBag,
  RESTORATION_PHASES,
} from '../../types/teardown';
import { PhaseTracker } from './PhaseTracker';
import { SubAssemblyGrid } from './SubAssemblyGrid';
import { HardwareBaggieModal } from './HardwareBaggieModal';
import { PhotoLogDrawer } from './PhotoLogDrawer';
import { TaskTracker } from './TaskTracker';
import { MechanicalButton } from '../common/MechanicalButton';
import {
  Wrench,
  Package,
  Camera,
  ListTodo,
  Plus,
  Search,
  ExternalLink,
  AlertTriangle,
  Layers,
} from 'lucide-react';

export type TeardownSubView =
  | 'sub_assemblies'
  | 'hardware_ledger'
  | 'tasks'
  | 'photo_ledger';

export interface TeardownViewProps {
  className?: string;
  defaultSubView?: TeardownSubView;
}

export const TeardownView: React.FC<TeardownViewProps> = ({
  className = '',
  defaultSubView = 'sub_assemblies',
}) => {
  const { state } = useRestoMod();
  const [activeSubView, setActiveSubView] = useState<TeardownSubView>(defaultSubView);

  // Hardware Baggie Modal State
  const [isBaggieModalOpen, setIsBaggieModalOpen] = useState(false);
  const [selectedBag, setSelectedBag] = useState<HardwareBag | null>(null);
  const [targetSubAssemblyId, setTargetSubAssemblyId] = useState<string | undefined>(undefined);

  // Hardware Ledger search & status filter
  const [baggieSearchTerm, setBaggieSearchTerm] = useState('');
  const [baggieStatusFilter, setBaggieStatusFilter] = useState('all');

  // Open modal to create a new baggie
  const handleOpenNewBaggie = (sub?: SubAssembly) => {
    setSelectedBag(null);
    setTargetSubAssemblyId(sub ? sub.id : state.subAssemblies[0]?.id);
    setIsBaggieModalOpen(true);
  };

  // Open modal to view/edit existing baggie
  const handleInspectBaggie = (bag: HardwareBag) => {
    setSelectedBag(bag);
    setTargetSubAssemblyId(bag.subAssemblyId);
    setIsBaggieModalOpen(true);
  };

  // Open modal for subassembly's first baggie, or create new if none exist
  const handleManageSubBags = (sub: SubAssembly) => {
    const existingBag = state.hardwareBags.find((b) => b.subAssemblyId === sub.id);
    if (existingBag) {
      handleInspectBaggie(existingBag);
    } else {
      handleOpenNewBaggie(sub);
    }
  };

  // Rollup metrics across vehicle
  const totalBins = state.subAssemblies.length;
  const installedBins = state.subAssemblies.filter((s) => s.status === 'installed').length;
  const totalBags = state.hardwareBags.length;
  const totalFasteners = state.hardwareBags.reduce(
    (acc, b) => acc + b.fasteners.reduce((fAcc, f) => fAcc + f.quantityOriginal, 0),
    0
  );
  const replacementFastenersCount = state.hardwareBags.reduce(
    (acc, b) =>
      acc +
      b.fasteners
        .filter((f) => f.replacementRequired)
        .reduce((fAcc, f) => fAcc + f.quantityOriginal, 0),
    0
  );
  const totalPhotos = state.photoLogs.length;
  const curatedPhotosCount = state.photoLogs.filter((p) => p.includeInDossier).length;
  const totalTasks = state.tasks.length;
  const completedTasks = state.tasks.filter((t) => t.status === 'completed').length;

  // Filtered baggies for master ledger
  const filteredBaggies = state.hardwareBags.filter((b) => {
    if (baggieStatusFilter !== 'all' && b.status !== baggieStatusFilter) return false;
    if (baggieSearchTerm.trim()) {
      const q = baggieSearchTerm.toLowerCase();
      const matchesLabel = b.bagLabel.toLowerCase().includes(q);
      const matchesBin = b.binLocation.toLowerCase().includes(q);
      const matchesNotes = b.notes.toLowerCase().includes(q);
      const matchesFastener = b.fasteners.some(
        (f) =>
          f.type.toLowerCase().includes(q) ||
          f.size.toLowerCase().includes(q) ||
          f.grade.toLowerCase().includes(q)
      );
      return matchesLabel || matchesBin || matchesNotes || matchesFastener;
    }
    return true;
  });

  return (
    <div className={`space-y-6 ${className}`}>
      {/* 1. Master Vehicle Context & Workshop Metrics Summary */}
      <div className="bg-shop-darkest border-2 border-shop-border rounded p-4 shadow-machined flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-safety-green"></span>
            <span className="text-xs font-mono font-bold uppercase tracking-wider text-shop-muted">
              Teardown Tracker &amp; Sub-Assembly Hardware Organizer
            </span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-shop-panel text-safety-amber border border-shop-border">
              R3 SYSTEM
            </span>
          </div>
          <h1 className="text-lg font-bold text-shop-highlight uppercase tracking-wide mt-1">
            {state.project.title}
          </h1>
          <p className="text-xs font-mono text-shop-muted mt-0.5">
            VIN: <strong className="text-shop-light">{state.project.vin}</strong> • Active Phase:{' '}
            <strong className="text-safety-amber">
              {RESTORATION_PHASES[state.activePhase]?.title}
            </strong>
          </p>
        </div>

        {/* Global Workshop Quick Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono shrink-0">
          <div className="bg-shop-panel px-3 py-2 rounded border border-shop-border">
            <span className="text-[10px] text-shop-muted uppercase block">STORAGE BINS</span>
            <div className="text-sm font-bold text-shop-highlight">
              {installedBins} / {totalBins}
            </div>
            <span className="text-[10px] text-safety-green">Installed on car</span>
          </div>

          <div className="bg-shop-panel px-3 py-2 rounded border border-shop-border">
            <span className="text-[10px] text-shop-muted uppercase block">HARDWARE BAGS</span>
            <div className="text-sm font-bold text-safety-amber">{totalBags} bags</div>
            <span className="text-[10px] text-shop-muted">
              {totalFasteners} fasteners ({replacementFastenersCount} replace)
            </span>
          </div>

          <div className="bg-shop-panel px-3 py-2 rounded border border-shop-border">
            <span className="text-[10px] text-shop-muted uppercase block">PHOTO EVIDENCE</span>
            <div className="text-sm font-bold text-blueprint-cyan">{totalPhotos} logs</div>
            <span className="text-[10px] text-shop-muted">
              {curatedPhotosCount} underwriting dossier
            </span>
          </div>

          <div className="bg-shop-panel px-3 py-2 rounded border border-shop-border">
            <span className="text-[10px] text-shop-muted uppercase block">TASKS DONE</span>
            <div className="text-sm font-bold text-shop-light">
              {completedTasks} / {totalTasks}
            </div>
            <span className="text-[10px] text-shop-muted">
              {totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0}% phase labor
            </span>
          </div>
        </div>
      </div>

      {/* 2. 7-Phase Restoration Pipeline Stepper */}
      <PhaseTracker />

      {/* 3. Sub-View Segmented Navigation Tabs (Min 44px touch targets) */}
      <div className="border-b border-shop-border flex items-center justify-between gap-2 overflow-x-auto pb-px">
        <div className="flex items-center gap-1.5 min-w-max">
          <button
            type="button"
            onClick={() => setActiveSubView('sub_assemblies')}
            className={`min-h-[44px] px-4 py-2 font-bold font-mono text-xs uppercase rounded-t border-t border-x transition-all duration-100 flex items-center gap-2 cursor-pointer ${
              activeSubView === 'sub_assemblies'
                ? 'bg-shop-panel text-safety-amber border-shop-border border-b-2 border-b-shop-panel shadow-[0_-2px_0_0_#f59e0b]'
                : 'bg-shop-dark text-shop-muted border-transparent hover:text-shop-light hover:bg-shop-well'
            }`}
          >
            <Package className="w-4 h-4" />
            <span>Storage Bins &amp; Containers ({state.subAssemblies.filter((s) => s.phase === state.activePhase).length})</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSubView('hardware_ledger')}
            className={`min-h-[44px] px-4 py-2 font-bold font-mono text-xs uppercase rounded-t border-t border-x transition-all duration-100 flex items-center gap-2 cursor-pointer ${
              activeSubView === 'hardware_ledger'
                ? 'bg-shop-panel text-safety-amber border-shop-border border-b-2 border-b-shop-panel shadow-[0_-2px_0_0_#f59e0b]'
                : 'bg-shop-dark text-shop-muted border-transparent hover:text-shop-light hover:bg-shop-well'
            }`}
          >
            <Wrench className="w-4 h-4" />
            <span>Hardware Baggie Ledger ({state.hardwareBags.length})</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSubView('tasks')}
            className={`min-h-[44px] px-4 py-2 font-bold font-mono text-xs uppercase rounded-t border-t border-x transition-all duration-100 flex items-center gap-2 cursor-pointer ${
              activeSubView === 'tasks'
                ? 'bg-shop-panel text-safety-amber border-shop-border border-b-2 border-b-shop-panel shadow-[0_-2px_0_0_#f59e0b]'
                : 'bg-shop-dark text-shop-muted border-transparent hover:text-shop-light hover:bg-shop-well'
            }`}
          >
            <ListTodo className="w-4 h-4" />
            <span>Restoration Tasks ({state.tasks.filter((t) => t.phase === state.activePhase).length})</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSubView('photo_ledger')}
            className={`min-h-[44px] px-4 py-2 font-bold font-mono text-xs uppercase rounded-t border-t border-x transition-all duration-100 flex items-center gap-2 cursor-pointer ${
              activeSubView === 'photo_ledger'
                ? 'bg-shop-panel text-safety-amber border-shop-border border-b-2 border-b-shop-panel shadow-[0_-2px_0_0_#f59e0b]'
                : 'bg-shop-dark text-shop-muted border-transparent hover:text-shop-light hover:bg-shop-well'
            }`}
          >
            <Camera className="w-4 h-4" />
            <span>Photo Proof Ledger ({state.photoLogs.filter((p) => p.phase === state.activePhase).length})</span>
          </button>
        </div>

        {/* Global Quick Action */}
        <div className="shrink-0 pb-1">
          <MechanicalButton
            variant="secondary"
            size="sm"
            icon={<Plus className="w-4 h-4 text-safety-amber" />}
            onClick={() => handleOpenNewBaggie()}
          >
            New Hardware Bag
          </MechanicalButton>
        </div>
      </div>

      {/* 4. Active Sub-View Viewport */}
      <div>
        {activeSubView === 'sub_assemblies' && (
          <SubAssemblyGrid
            onManageHardwareBags={handleManageSubBags}
            onAddHardwareBag={handleOpenNewBaggie}
          />
        )}

        {activeSubView === 'tasks' && <TaskTracker />}

        {activeSubView === 'photo_ledger' && <PhotoLogDrawer />}

        {activeSubView === 'hardware_ledger' && (
          <div className="space-y-4">
            {/* Toolbar for Hardware Ledger */}
            <div className="bg-shop-panel border border-shop-border rounded p-4 shadow-machined flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-wrap flex-1">
                {/* Search Baggies */}
                <div className="relative min-w-[240px] flex-1 max-w-sm">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-shop-muted pointer-events-none" />
                  <input
                    type="text"
                    value={baggieSearchTerm}
                    onChange={(e) => setBaggieSearchTerm(e.target.value)}
                    placeholder="Search bags, sizes, grades, bins..."
                    className="w-full min-h-[44px] pl-9 pr-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light placeholder:text-shop-muted focus:outline-none focus:border-safety-amber font-mono"
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={baggieStatusFilter}
                  onChange={(e) => setBaggieStatusFilter(e.target.value)}
                  className="min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light focus:outline-none focus:border-safety-amber font-mono cursor-pointer"
                >
                  <option value="all">All Baggie Statuses</option>
                  <option value="tagged">Tagged As-Found</option>
                  <option value="cleaning_prep">Cleaning &amp; Degreasing</option>
                  <option value="plated_restored">Plated / Restored</option>
                  <option value="replaced">New Fasteners Acquired</option>
                  <option value="reinstalled">Torqued &amp; Reinstalled</option>
                </select>
              </div>

              <MechanicalButton
                variant="primary"
                size="default"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => handleOpenNewBaggie()}
              >
                Create Hardware Baggie
              </MechanicalButton>
            </div>

            {/* Baggies Table */}
            {filteredBaggies.length === 0 ? (
              <div className="bg-shop-dark border border-shop-border rounded p-12 text-center space-y-3">
                <div className="w-12 h-12 bg-shop-panel rounded-full flex items-center justify-center mx-auto text-shop-muted border border-shop-border">
                  <Wrench className="w-6 h-6" />
                </div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-shop-highlight">
                  No Hardware Baggies Found
                </h3>
                <p className="text-xs text-shop-muted max-w-md mx-auto">
                  {baggieSearchTerm || baggieStatusFilter !== 'all'
                    ? 'No hardware baggies match your search query.'
                    : 'No hardware baggies recorded for this restoration project yet. Create your first baggie to log fastener thread sizes, torque specs, and plating.'}
                </p>
                <div className="pt-2">
                  <MechanicalButton variant="secondary" size="sm" onClick={() => handleOpenNewBaggie()}>
                    Create Hardware Baggie
                  </MechanicalButton>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredBaggies.map((bag) => {
                  const matchingSub = state.subAssemblies.find((s) => s.id === bag.subAssemblyId);
                  const totalCount = bag.fasteners.reduce((acc, f) => acc + f.quantityOriginal, 0);
                  const usableCount = bag.fasteners.reduce((acc, f) => acc + f.quantityUsable, 0);
                  const replaceCount = bag.fasteners.reduce(
                    (acc, f) =>
                      acc + (f.replacementRequired ? f.quantityOriginal - f.quantityUsable || f.quantityOriginal : 0),
                    0
                  );

                  return (
                    <div
                      key={bag.id}
                      className="bg-shop-dark border border-shop-border rounded p-4 shadow-machined space-y-3 hover:bg-shop-panel/50 transition-all duration-150"
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        {/* Baggie Header & Tags */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs font-bold text-safety-amber bg-shop-darkest px-2.5 py-1 rounded border border-shop-border">
                              {bag.binLocation}
                            </span>
                            <span className="text-sm font-bold text-shop-highlight uppercase">
                              {bag.bagLabel}
                            </span>
                            <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-shop-well text-shop-muted border border-shop-border">
                              {bag.status.replace('_', ' ')}
                            </span>
                          </div>

                          {matchingSub && (
                            <div className="text-xs font-mono text-safety-amber flex items-center gap-1.5 pt-0.5">
                              <Layers className="w-3.5 h-3.5" />
                              <span>
                                Storage Bin: [{matchingSub.binNumber}] {matchingSub.name} ({matchingSub.storageLocation})
                              </span>
                            </div>
                          )}

                          {bag.torqueSpec && (
                            <div className="text-xs font-mono text-safety-cyan">
                              Torque Spec: <strong>{bag.torqueSpec}</strong>
                            </div>
                          )}

                          {bag.notes && (
                            <p className="text-xs text-shop-muted line-clamp-1">{bag.notes}</p>
                          )}
                        </div>

                        {/* Fastener Tally & Inspect Action */}
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right text-xs font-mono bg-shop-darkest px-3 py-1.5 rounded border border-shop-border">
                            <div className="text-shop-light font-bold">
                              {usableCount} / {totalCount} Usable
                            </div>
                            {replaceCount > 0 ? (
                              <span className="text-[10px] font-bold text-safety-red flex items-center justify-end gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {replaceCount} need replacement
                              </span>
                            ) : (
                              <span className="text-[10px] text-safety-green">Originals OK</span>
                            )}
                          </div>

                          <MechanicalButton
                            variant="secondary"
                            size="sm"
                            icon={<ExternalLink className="w-4 h-4" />}
                            onClick={() => handleInspectBaggie(bag)}
                          >
                            Inspect Specs
                          </MechanicalButton>
                        </div>
                      </div>

                      {/* Fastener Line Items Overview */}
                      <div className="bg-shop-darkest border border-shop-border rounded p-2.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {bag.fasteners.map((f) => (
                          <div
                            key={f.id}
                            className="bg-shop-panel/70 p-2 rounded border border-shop-border/40 text-xs font-mono space-y-0.5"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-shop-light truncate mr-2">
                                {f.quantityOriginal}x {f.type}
                              </span>
                              {f.replacementRequired && (
                                <span className="text-[9px] font-bold text-safety-red bg-safety-red/10 px-1 rounded border border-safety-red/30 uppercase">
                                  REPLACE
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-safety-amber font-bold">
                              {f.size}
                            </div>
                            <div className="text-[10px] text-shop-muted">
                              {f.grade} • {f.finish}
                              {f.headmarkNotes ? ` • ${f.headmarkNotes}` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 5. Master Hardware Baggie Modal Dialog */}
      <HardwareBaggieModal
        isOpen={isBaggieModalOpen}
        onClose={() => setIsBaggieModalOpen(false)}
        bag={selectedBag}
        initialSubAssemblyId={targetSubAssemblyId}
      />
    </div>
  );
};
